import React, { useState, useEffect, useCallback } from "react";
import { clearSession } from "../shared/_helpers/session";
import TableComponent from "./TableComponent";
import { useSnackbar } from "notistack";
import Modal from "react-bootstrap/Modal";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { Link, useNavigate } from "react-router-dom";
import Loader from "./Loader";
import Pagination from "./Pagination";
import {
  checkBlockedStatus,
  deleteInvestmentTool,
  getAllInvestmentTools,
  toggleInvestmentToolPriority,
  toggleInvestmentToolStatus,
  updateInvestmentTool,
  uploadInvestmentTool,
} from "../shared/_services/common.services";
import Cropper from "react-easy-crop";

const ALLOWED_INVESTMENT_TOOL_IMAGE_TYPES = ["image/png", "image/jpeg", "image/jpg"];
const MAX_INVESTMENT_TOOL_FILE_BYTES = 5 * 1024 * 1024;

/** @returns {string|null} Error message or null if valid */
function getInvestmentToolFileValidationError(isEditing, file) {
  if (!isEditing && !file) {
    return "File is required when adding a new tool";
  }
  if (file instanceof File) {
    if (!ALLOWED_INVESTMENT_TOOL_IMAGE_TYPES.includes(file.type)) {
      return "Only PNG,JPEG and JPG files are allowed";
    }
    if (file.size > MAX_INVESTMENT_TOOL_FILE_BYTES) {
      return "File size must be less than 5MB";
    }
  }
  return null;
}

function buildInvestmentToolFormData(values) {
  const camelCasedName = values.name
    .split(" ")
    .filter((word) => word.trim() !== "")
    .map((word) => word[0].toUpperCase() + word.slice(1).toLowerCase())
    .join("");
  const formData = new FormData();
  formData.append("name", camelCasedName);
  if (values.file instanceof File) {
    formData.append("investment_tool_image", values.file);
  }
  formData.append("websiteUrl", values.websiteUrl);
  return formData;
}

async function uploadOrUpdateInvestmentTool(formData, isEditing, selectedTool) {
  if (isEditing && selectedTool) {
    return updateInvestmentTool(selectedTool._id, formData);
  }
  return uploadInvestmentTool(formData);
}

/** @returns {string|null} Error message for snackbar, or null if file can be cropped */
function getCropPickerFileSelectionError(file) {
  if (!ALLOWED_INVESTMENT_TOOL_IMAGE_TYPES.includes(file.type)) {
    return "Only PNG, JPEG, and JPG files are allowed";
  }
  if (file.size > MAX_INVESTMENT_TOOL_FILE_BYTES) {
    return "File size must be less than 5MB";
  }
  return null;
}

function loadFileIntoCropModal(file, setImageSrc, setCropModalOpen) {
  const reader = new FileReader();
  reader.onload = () => {
    setImageSrc(reader.result);
    setCropModalOpen(true);
  };
  reader.readAsDataURL(file);
}

const InvestmentTool = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [toolsData, setToolsData] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [confirmTogglePopupShow, setConfirmTogglePopupShow] = useState(false);
  const [confirmPriorityPopupShow, setConfirmPriorityPopupShow] = useState(false);
  const [confirmDeletePopupShow, setConfirmDeletePopupShow] = useState(false);
  const [selectedTool, setSelectedTool] = useState(null);
  const [toggleAction, setToggleAction] = useState("");
  const [priorityAction, setPriorityAction] = useState("");
  const [selectedFileName, setSelectedFileName] = useState("");
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage] = useState(10);
  const [isEditing, setIsEditing] = useState(false);
  const headers = ["Tool Name", "Image", "Status", "Toggle", "Top Priority", "Edit"];


  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);
  
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  const getToolsData = async () => {
    setShowLoader(true);
    try {
      const response = await getAllInvestmentTools();
      const normalizedTools = response.data.tools.map((tool) => ({
        ...tool,
        filePath: tool.filePath.replace(/\\/g, "/"),
      }));
      setToolsData(normalizedTools);
      setShowLoader(false);
    } catch (error) {
      // The read is authenticated now, so 401/403 are responses this screen can
      // actually receive. axios rejects on both, which is why the old
      // `response.status === 401` branch above the catch never ran.
      if (error.response?.status === 401) {
        clearSession();
        enqueueSnackbar("Your session has expired.", {
          variant: "error",
          anchorOrigin: { horizontal: "right", vertical: "top" },
        });
        navigate("/login");
      } else if (error.response?.status === 403) {
        enqueueSnackbar("You do not have permission to manage investment tools", {
          variant: "error",
        });
      } else {
        enqueueSnackbar("Failed to fetch investment tools", { variant: "error" });
      }
      setShowLoader(false);
    }

  };

  const checkBlocked = async () => {
    const userId = JSON.parse(localStorage.getItem("currentUser"))?.user?._id;
    if (!userId) {
      navigate("/login");
      return;
    }
    try {
      const response = await checkBlockedStatus(userId);
      if (response.status === 401) {
        clearSession();
        navigate("/login");
      } else {
        const { isBlocked, isDeleted } = response.data;
        if (isBlocked || isDeleted) {
          clearSession();
          navigate("/login");
        }
      }
    } catch (error) {
      console.error("Error checking blocked status:", error);
    }
  };

  useEffect(() => {
    checkBlocked();
    getToolsData();
  }, []);

  


  const validationSchema = Yup.object().shape({
  name: Yup.string()
    .required("Tool name is required")
    .test("no-leading-space", "Tool name cannot start with a space", (value) => {
      return !value.startsWith(" ");
    })
    .min(3, "Tool name must be at least 3 characters")
    .max(50, "Tool name must be at most 50 characters"),
  websiteUrl: Yup.string()
    .matches(
      /^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}(\/.*)?$/,
      "Please enter a valid URL (e.g., https://example.com)"
    ),
});

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    const fileError = getInvestmentToolFileValidationError(isEditing, values.file);
    if (fileError) {
      enqueueSnackbar(fileError, { variant: "error" });
      setSubmitting(false);
      setShowLoader(false);
      return;
    }

    setShowLoader(true);
    const formData = buildInvestmentToolFormData(values);

    try {
      const response = await uploadOrUpdateInvestmentTool(
        formData,
        isEditing,
        selectedTool
      );

      if (response.status === 200) {
        enqueueSnackbar(
          `Investment tool ${isEditing ? "updated" : "uploaded"} successfully`,
          { variant: "success" }
        );
        getToolsData();
        setShowModal(false);
        resetForm();
        setSelectedFileName("");
        setIsEditing(false);
        setSelectedTool(null);
      } else {
        enqueueSnackbar(
          `Failed to ${isEditing ? "update" : "upload"} investment tool: ${response.data?.error || response.statusText}`,
          { variant: "error" }
        );
      }
    } catch (error) {
      enqueueSnackbar(
        `Failed to ${isEditing ? "update" : "upload"} investment tool: ${error.response?.data?.error || error.message}`,
        { variant: "error" }
      );
    } finally {
      setSubmitting(false);
      setShowLoader(false);
    }
  };

  const handleToggleStatus = async () => {
    setConfirmTogglePopupShow(false);
    setShowLoader(true);
    try {
      const response = await toggleInvestmentToolStatus(
        selectedTool._id,
        toggleAction === "enable"
      );

      if (response.status === 200) {
        enqueueSnackbar(`Tool ${toggleAction === "enable" ? "enabled" : "disabled"} successfully`, {
          variant: "success",
        });
        getToolsData();
      }
      else {
        enqueueSnackbar(`Failed to ${toggleAction} tool`, { variant: "error" });
      }
    } catch (error) {

      enqueueSnackbar(`Failed to ${toggleAction} tool: ${error.response?.data?.message || error.message}`, {
        variant: "error",
      });
    } finally {
      setShowLoader(false);
    }
  };

  const handleTogglePriority = async () => {
    setConfirmPriorityPopupShow(false);
    setShowLoader(true);
    try {
      const response = await toggleInvestmentToolPriority(
        selectedTool._id,
        priorityAction === "enable"
      );
      if (response.status === 200) {
        enqueueSnackbar(`Tool ${priorityAction === "enable" ? "marked" : "unmarked"} as top priority successfully`, {
          variant: "success",
        });
        getToolsData();
      } else {
        enqueueSnackbar(`Failed to ${priorityAction} tool priority: ${response.data?.error || response.statusText}`, {
          variant: "error",
        });
      }
    } catch (error) {
      enqueueSnackbar(`Failed to ${priorityAction} tool priority: ${error.response?.data?.error || error.message}`, {
        variant: "error",
      });
    } finally {
      setShowLoader(false);
    }
  };

  const handleDeleteTool = async () => {
    setConfirmDeletePopupShow(false);
    setShowLoader(true);
    try {
      const response = await deleteInvestmentTool(selectedTool._id);
      if (response.status === 200) {
        enqueueSnackbar("Tool deleted successfully", { variant: "success" });
        getToolsData();
      } else {
        enqueueSnackbar(`Failed to delete tool: ${response.data?.error || response.statusText}`, {
          variant: "error",
        });
      }
    } catch (error) {
      enqueueSnackbar(`Failed to delete tool: ${error.response?.data?.error || error.message}`, {
        variant: "error",
      });
    } finally {
      setShowLoader(false);
    }
  };

  const handleEditTool = (tool) => {
    
    setSelectedTool(tool);
    setIsEditing(true);
    setSelectedFileName(tool.fileName || "");
    setShowModal(true);
  };

  const getCroppedImg = (imageSrc, croppedAreaPixels) => {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.src = imageSrc;
    image.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = 150; // fixed width
      canvas.height = 50; // fixed height
      const ctx = canvas.getContext("2d");

      ctx.drawImage(
        image,
        croppedAreaPixels.x,
        croppedAreaPixels.y,
        croppedAreaPixels.width,
        croppedAreaPixels.height,
        0,
        0,
        150,
        50
      );

      canvas.toBlob((blob) => {
        resolve(blob);
      }, "image/png");
    };
    image.onerror = (err) => reject(err);
  });
};


  const openConfirmTogglePopup = (tool) => {
    setSelectedTool(tool);
    setToggleAction(tool.isEnabled ? "disable" : "enable");
    setConfirmTogglePopupShow(true);
  };

  const openConfirmPriorityPopup = (tool) => {
    setSelectedTool(tool);
    setPriorityAction(tool.isTopPriority ? "disable" : "enable");
    setConfirmPriorityPopupShow(true);
  };



  const handleAddToolClick = () => {
    setIsEditing(false);
    setSelectedTool(null);
    setShowModal(true);
    setSelectedFileName("");
  };

  const closeModal = () => {
    setIsEditing(false);
    setSelectedTool(null);
    setShowModal(false);
    setSelectedFileName("");
  };

  const handleCropPickerFileChange = useCallback(
    (event) => {
      const input = event.currentTarget;
      const file = input.files?.[0];
      if (!file) return;

      const errorMessage = getCropPickerFileSelectionError(file);
      if (errorMessage) {
        enqueueSnackbar(errorMessage, { variant: "error" });
        input.value = "";
        return;
      }

      loadFileIntoCropModal(file, setImageSrc, setCropModalOpen);
    },
    [enqueueSnackbar]
  );

  const confirmTogglePopupClose = () => {
    setConfirmTogglePopupShow(false);
  };

  const confirmPriorityPopupClose = () => {
    setConfirmPriorityPopupShow(false);
  };

  const confirmDeletePopupClose = () => {
    setConfirmDeletePopupShow(false);
  };

   const getInitialValues = () => {
    if (isEditing && selectedTool) {
      // Extract name from fileName (remove extension)
      const nameWithoutExtension = selectedTool.fileName.replace(/\.[^/.]+$/, "");
      return {
        name: nameWithoutExtension,
        file: null, // File will be optional for editing
        websiteUrl: selectedTool.websiteUrl || "",
      };
    }
    return {
      name: "",
      file: null,
      websiteUrl: "",
    };
  };

  // Pagination
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const safeToolsData = Array.isArray(toolsData) ? toolsData : [];
  const currentRecords = safeToolsData.slice(indexOfFirstRecord, indexOfLastRecord);
  const nPages = Math.ceil(safeToolsData.length / recordsPerPage);

  return (
    <>
      <div className="page-header d-flex align-items-center justify-content-between">
        <h2>Investment Tools</h2>
        <button type="button" className="as-btn-primary btn-sharp mt-1 active" onClick={handleAddToolClick}>
          Add Tool
        </button>
      </div>
      <div className="p-4">
        {showLoader ? (
          <Loader />
        ) : (
          <>
            <TableComponent
              headers={headers}
              data={currentRecords}
              test={"investment-tool"}
              onToggleToolClick={openConfirmTogglePopup}
              onTogglePriorityClick={openConfirmPriorityPopup}
              onEditToolClick={handleEditTool}
            // onDeleteToolClick={openConfirmDeletePopup}
            />
            <Pagination nPages={nPages} currentPage={currentPage} setCurrentPage={setCurrentPage} />
          </>
        )}
      </div>

      {/* Add Tool Modal */}
      <Modal
        size="md"
        className="editProfilePopup"
        show={showModal}
        onHide={closeModal}
        aria-labelledby="modalLabel"
        backdrop="static"
        keyboard={false}
        centered
      >
        <div className="modal-body">
          <div className="d-flex justify-content-between">
            <h4 className="mt-1 mb-3">{isEditing? "Edit":"Add"}Add Investment Tool</h4>
            <Link className="close-popup-btn" onClick={closeModal}>
              <i className="fa fa-times"></i>
            </Link>
          </div>
          <Formik
            key={isEditing ? `edit-${selectedTool?._id}` : 'add'}
            initialValues={getInitialValues()}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            enableReinitialize={true}
          >
            {({errors, touched, isSubmitting, setFieldValue, values, isValid, validateForm }) => (
              <Form>
                <div className="form-group">
                  <label htmlFor="name">Tool Name</label>
                  <Field
                    type="text"
                    id="name"
                    name="name"
                    placeholder="Tool Name"
                    className={`form-control ${errors.name && touched.name ? "is-invalid" : ""}`}
                  />
                  <ErrorMessage name="name" component="div" className="invalid-feedback" />
                </div>
                <div className="form-group">
                  <label htmlFor="websiteUrl">Website URL</label>
                  <Field
                    type="url"
                    id="websiteUrl"
                    name="websiteUrl"
                    placeholder="https://example.com"
                    className={`form-control ${errors.websiteUrl && touched.websiteUrl ? "is-invalid" : ""}`}
                  />
                  <ErrorMessage name="websiteUrl" component="div" className="invalid-feedback" />
                </div>
                <div className="form-group">
                  <label htmlFor="file">Upload File</label>
                  <div className="d-flex align-items-center">
                    <label htmlFor="file" className="upload-icon cursor-pointer">
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M12 2V12M8 5L12 1L16 5M4 16H20V20H4V16Z"
                          stroke="#1A1D43"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </label>
                    <input
                      type="file"
                      id="file"
                      name="file"
                      className="d-none"
                      onChange={handleCropPickerFileChange}
                      accept=".png, .jpg, .jpeg, image/png, image/jpg, image/jpeg"
                    />
                    <span className="text-muted margin-left:20px">
                      {selectedFileName ||  (isEditing && selectedTool ? selectedTool.fileName : "No file chosen")}
                    </span>
                  </div>
                  <ErrorMessage name="file" component="div" className="invalid-feedback" />
                </div>
                <button type="submit" disabled={isSubmitting} className="as-btn-primary w-100 mt-2"
                >
                  {isEditing? "Update": "Save"}
                </button>



                {/* Crop Tool Popup Modal */}
                <Modal show={cropModalOpen} onHide={() => setCropModalOpen(false)} centered size="lg">
                  <div className="modal-body" style={{ height: 400, position: "relative" }}>
                    {imageSrc && (
                      <Cropper
                        image={imageSrc}
                        crop={crop}
                        zoom={zoom}
                        aspect={150 / 50}  // maintain ratio
                        onCropChange={setCrop}
                        onZoomChange={setZoom}
                        onCropComplete={(_, croppedAreaPixels) => setCroppedAreaPixels(croppedAreaPixels)}
                      />
                    )}
                  </div>
                  <div className="d-flex justify-content-end p-3">
                    <button
                      className="as-btn-primary"
                      onClick={async () => {
                        const blob = await getCroppedImg(imageSrc, croppedAreaPixels);
                        const file = new File([blob], "cropped.png", { type: "image/png" });
                        
                        setFieldValue("file", file); // push into Formik
                        setSelectedFileName(file.name);
                        setCropModalOpen(false);
                      }}
                    >
                      Crop & Save
                    </button>
                  </div>
                </Modal>

              </Form>
            )}
          </Formik>
        </div>
      </Modal>

      {/* Toggle Status Popup Modal */}
      <Modal
        size="md"
        className="confirmBlockPopup customMiniPopup"
        show={confirmTogglePopupShow}
        onHide={confirmTogglePopupClose}
        aria-labelledby="confirmTogglePopupLabel"
        backdrop="static"
        keyboard={false}
        centered
      >
        <div className="modal-body">
          <div className="d-flex justify-content-between">
            <h4 className="mt-3"></h4>
            <Link className="close-popup-btn" onClick={confirmTogglePopupClose}>
              <i className="fa fa-times"></i>
            </Link>
          </div>
          <h4 className="mt-3 mb-3 text-center">{`You are attempting to ${toggleAction} ${selectedTool ? selectedTool.fileName.replace(/\.[^/.]+$/, "") : ""}.`}</h4>
          <h5 className="text-center f-700">Are you sure?</h5>
          <div className="text-center mt-4 mb-3">
            <button className="as-btn-primary w-75" onClick={handleToggleStatus}>
              {toggleAction === "enable" ? "Enable" : "Disable"}
            </button>
          </div>
        </div>
      </Modal>

      {/* Toggle Priority Popup Modal */}
      <Modal
        size="md"
        className="confirmBlockPopup customMiniPopup"
        show={confirmPriorityPopupShow}
        onHide={confirmPriorityPopupClose}
        aria-labelledby="confirmPriorityPopupLabel"
        backdrop="static"
        keyboard={false}
        centered
      >
        <div className="modal-body">
          <div className="d-flex justify-content-between">
            <h4 className="mt-3"></h4>
            <Link className="close-popup-btn" onClick={confirmPriorityPopupClose}>
              <i className="fa fa-times"></i>
            </Link>
          </div>
          <h4 className="mt-3 mb-3 text-center">{`You are attempting to ${priorityAction === "enable" ? "mark" : "unmark"} ${selectedTool ? selectedTool.fileName.replace(/\.[^/.]+$/, "") : ""} as top priority.`}</h4>
          <h5 className="text-center f-700">Are you sure?</h5>
          <div className="text-center mt-4 mb-3">
            <button className="as-btn-primary w-75" onClick={handleTogglePriority}>
              {priorityAction === "enable" ? "Mark" : "Unmark"}
            </button>
          </div>
        </div>
      </Modal>

      {/* Delete Tool Popup Modal */}
      <Modal
        size="md"
        className="confirmBlockPopup customMiniPopup"
        show={confirmDeletePopupShow}
        onHide={confirmDeletePopupClose}
        aria-labelledby="confirmDeletePopupLabel"
        backdrop="static"
        keyboard={false}
        centered
      >
        <div className="modal-body">
          <div className="d-flex justify-content-between">
            <h4 className="mt-3"></h4>
            <Link className="close-popup-btn" onClick={confirmDeletePopupClose}>
              <i className="fa fa-times"></i>
            </Link>
          </div>
          <h4 className="mt-3 mb-3 text-center">{`You are attempting to delete ${selectedTool ? selectedTool.fileName.replace(/\.[^/.]+$/, "") : ""}.`}</h4>
          <h5 className="text-center f-700">Are you sure?</h5>
          <div className="text-center mt-4 mb-3">
            <button className="as-btn-primary w-75" onClick={handleDeleteTool}>
              Delete
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default InvestmentTool;